
//2d mesh geometry, output: [x1, y1, x2, y2, ...]

import earcut from 'earcut';
import polygonClipping from 'polygon-clipping';

import { XY } from './V2.js';
import { G2, type BOX, type XYZR, type XYZRT } from './G2.js';

export type { BOX, XYZR, XYZRT };

import { Rndm } from './Rndm.js';
import { Conv } from './Conv.js';

type Pair = [number, number];
type Ring = Pair[];
export type MeshPolygon = Ring[];
export type MeshMultiPolygon = MeshPolygon[];

function tri_to_polygon(mesh: Float32Array, i: number): MeshPolygon {
    const o = i * 6;
    return [[[mesh[o], mesh[o + 1]], [mesh[o + 2], mesh[o + 3]], [mesh[o + 4], mesh[o + 5]], [mesh[o], mesh[o + 1]]]];
}

function mesh_to_multipolygon(mesh: Float32Array): MeshMultiPolygon {
    const count = Math.floor(mesh.length / 6);
    const polys: MeshPolygon[] = [];
    for (let i = 0; i < count; i++) polys.push(tri_to_polygon(mesh, i));
    return polys;
}

function flatten_polygon(poly: MeshPolygon): { vertices: number[]; holes: number[] } {
    const vertices: number[] = [];
    const holes: number[] = [];
    for (let r = 0; r < poly.length; r++) {
        if (r > 0) holes.push(vertices.length / 2);
        const ring = poly[r];
        const len = ring.length > 1 &&
            ring[0][0] === ring[ring.length - 1][0] &&
            ring[0][1] === ring[ring.length - 1][1]
            ? ring.length - 1 : ring.length;
        for (let i = 0; i < len; i++) {
            vertices.push(ring[i][0], ring[i][1]);
        }
    }
    return { vertices, holes };
}

function triangulate_multi(multi: MeshMultiPolygon): Float32Array {
    const out: number[] = [];
    for (const poly of multi) {
        const { vertices, holes } = flatten_polygon(poly);
        const indices = earcut(vertices, holes.length ? holes : undefined, 2);
        for (let i = 0; i < indices.length; i++) {
            const vi = indices[i] * 2;
            out.push(vertices[vi], vertices[vi + 1]);
        }
    }
    return new Float32Array(out);
}


const TINY_TRI_AREA = 1e-8;

export const M2 = {
    pp: Math.PI * 2,
    sets: function (ia: Float32Array, ia_new: Float32Array): void {
        ia.set(ia_new);
    },
    box: {
        calc: function (ia: Float32Array): BOX {
            const extrem = { x: Infinity, y: Infinity, xx: -Infinity, yy: -Infinity };
            for (let i = 0; i < ia.length; i += 2) {
                if (ia[i] < extrem.x) extrem.x = ia[i];
                if (ia[(i + 1)] < extrem.y) extrem.y = ia[(i + 1)];
                if (ia[i] > extrem.xx) extrem.xx = ia[i];
                if (ia[(i + 1)] > extrem.yy) extrem.yy = ia[(i + 1)];
            };
            return extrem;
        },
        merge: function (box1: BOX, box2: BOX): BOX {
            const output = { x: box2.x, y: box2.y, xx: box2.xx, yy: box2.yy };
            if (box1.x < box2.x) output.x = box1.x;
            if (box1.xx > box2.xx) output.xx = box1.xx;
            if (box1.y < box2.y) output.y = box1.y;
            if (box1.yy > box2.yy) output.yy = box1.yy;
            return output;
        },
        xy: function (x: Float32Array, y: Float32Array): BOX { //two arrays of coordinates
            const extrem = { x: Infinity, y: Infinity, xx: -Infinity, yy: -Infinity };
            for (let i = 0; i < x.length; i += 2) {
                if (x[i] < extrem.x) extrem.x = x[i];
                if (y[i] < extrem.y) extrem.y = y[i];
                if (x[i] > extrem.xx) extrem.xx = x[i];
                if (y[i] > extrem.yy) extrem.yy = y[i];
            };
            return extrem;
        },
        expand: function (combined: BOX, box: BOX, world: XYZR): void {
            const corners = G2.transform.points([
                { x: box.x, y: box.y },
                { x: box.xx, y: box.y },
                { x: box.xx, y: box.yy },
                { x: box.x, y: box.yy },
            ], world);
            for (const c of corners) {
                if (c.x < combined.x) combined.x = c.x;
                if (c.x > combined.xx) combined.xx = c.x;
                if (c.y < combined.y) combined.y = c.y;
                if (c.y > combined.yy) combined.yy = c.y;
            }
        },
        set: function (target: BOX, from: BOX): void {
            target.x = from.x;
            target.y = from.y;
            target.xx = from.xx;
            target.yy = from.yy;
        }
    },
    get: {
        normals: function (a: number[], b: number[], lineWidth = 0.02): number[] {
            const dx = b[0] - a[0];
            const dy = b[1] - a[1];
            const len = Math.sqrt(dx * dx + dy * dy);
            return [-dy / len * lineWidth * 0.5, dx / len * lineWidth * 0.5];
        },
        distance: function (x1: number, y1: number, x2: number, y2: number) { //Euclidean
            const dx = x2 - x1;
            const dy = y2 - y1;
            return Math.sqrt(dx * dx + dy * dy);
        },
        random_colors: function (mesh: ArrayLike<number>, alpha = 1): Float32Array {

            const vertexCount = mesh.length / 2; // Total vertices
            const triangleCount = vertexCount / 3; // Assuming 3 vertices per triangle
            const colors = new Float32Array(vertexCount * 4); // 4 components (RGBA) per vertex

            for (let triangleIndex = 0; triangleIndex < triangleCount; triangleIndex++) {

                const r = Math.random();
                const g = Math.random();
                const b = Math.random();

                for (let vertexInTriangle = 0; vertexInTriangle < 3; vertexInTriangle++) {

                    const vertexIndex = triangleIndex * 3 + vertexInTriangle;
                    const colorIndex = vertexIndex * 4;

                    colors[colorIndex] = r;
                    colors[colorIndex + 1] = g;
                    colors[colorIndex + 2] = b;
                    colors[colorIndex + 3] = alpha;
                }
            }
            return colors;
        },
    },
    new: {
        offsets: function (ia: Float32Array, x = 0, y = 0): Float32Array { //done
            const output = new Float32Array(ia.length);
            for (let i = 0; i < ia.length; i += 2) {
                output[i] = ia[i] + x;
                output[(i + 1)] = ia[(i + 1)] + y;
            }
            return output;
        },
        rotates: function (ia: ArrayLike<number>, r = 0.0): Float32Array {  //done
            const output = new Float32Array(ia.length);
            const radian = M2.pp * r;
            const cos = Math.cos(radian);
            const sin = Math.sin(radian);
            for (let i = 0; i < ia.length; i += 2) {
                const x = ia[i];
                const y = ia[i + 1];
                output[i] = x * cos - y * sin;
                output[i + 1] = x * sin + y * cos;
            }
            return output;
        },
        scales: function (ia: ArrayLike<number>, s = 1.0): Float32Array {  //done
            const output = new Float32Array(ia.length);
            for (let i = 0; i < ia.length; i++) {
                output[i] = ia[i] * s;
            }
            return output;
        },
        noise: function (ia: ArrayLike<number>, time = 0, options: any = {}): Float32Array {  //done
            const { amp = 0.025, resolution = 150, speed = 0.1 } = options;
            const output = new Float32Array(ia);
            for (let i = 0; i < ia.length; i++) {
                output[i] += Rndm.fast.GetNoise(ia[i] * resolution, time * speed) * amp;
            }
            return output;
        },
        transforms: function (params: { mesh: Float32Array, rotation?: number, x?: number, y?: number, scale?: number }): Float32Array {  //done
            const { mesh, rotation = 0.0, x = 0.0, y = 0.0, scale = 1.0 } = params;
            const angle = rotation * M2.pp;  // Convert 0-1 to 0-2π radians
            const cos = Math.cos(angle) * scale;
            const sin = Math.sin(angle) * scale;
            const output = new Float32Array(mesh.length);
            for (let i = 0; i < mesh.length; i += 2) {
                const originalX = mesh[i];
                const originalY = mesh[i + 1];
                output[i] = originalX * cos - originalY * sin + x;
                output[i + 1] = originalX * sin + originalY * cos + y;
            }
            return output;
        },
        colors: function (mesh: ArrayLike<number>, ia: any[]): Float32Array { //ia = [{times: 3, color: [0.5,1,1,1]}, {times: 30, color: [1,1,1,1]}, ...

            const vertexCount = mesh.length / 2; //XY
            const colors_array = new Float32Array(vertexCount * 4); //RGBA
            let vertexIndex = 0;

            for (const o of ia) {
                for (let triangle = 0; triangle < o.times; triangle++) {
                    for (let vertex = 0; vertex < 3; vertex++) {
                        const baseIndex = (vertexIndex + vertex) * 4;
                        colors_array[baseIndex + 0] = o.color[0];
                        colors_array[baseIndex + 1] = o.color[1];
                        colors_array[baseIndex + 2] = o.color[2];
                        colors_array[baseIndex + 3] = o.color[3];
                    }
                    vertexIndex += 3;
                }
            }
            return colors_array;
        },

        truncate: function (values: Float32Array, decimals: number = 4): Float32Array {
            const factor = Math.pow(10, decimals);
            const out = new Float32Array(values.length);
            for (let i = 0; i < values.length; i++) {
                out[i] = Math.round(values[i] * factor) / factor;
            }
            return out;
        }
    },
    set: {

        offsets: function (ia: Float32Array, x = 0, y = 0) { //done
            for (let i = 0; i < ia.length; i += 2) {
                ia[i] = ia[i] + x;
                ia[(i + 1)] = ia[(i + 1)] + y;
            }
        },

        rotates: function (ia: Float32Array, r = 0.0) {  //done
            const radian = M2.pp * r;
            const cos = Math.cos(radian);
            const sin = Math.sin(radian);

            for (let i = 0; i < ia.length; i += 2) {
                const x = ia[i];
                const y = ia[i + 1];
                ia[i] = x * cos - y * sin;     // new x
                ia[i + 1] = x * sin + y * cos; // new y
            }
        },

        adds: function (ia: Float32Array, add = 0) {  //done
            for (let i = 0; i < ia.length; i++) {
                ia[i] += add;
            }
        },

        scales: function (ia: Float32Array, s = 1.0) {  //done
            for (let i = 0; i < ia.length; i++) {
                ia[i] *= s;
            }
        },

        transforms: function (params: { mesh: Float32Array, rotation?: number, x?: number, y?: number, scale?: number }) {  //done
            const { mesh, rotation = 0.0, x = 0.0, y = 0.0, scale = 1.0 } = params;
            const angle = rotation * M2.pp; // Convert 0-1 to radians
            const cos = Math.cos(angle) * scale;
            const sin = Math.sin(angle) * scale;
            for (let i = 0; i < mesh.length; i += 2) {
                const originalX = mesh[i];
                const originalY = mesh[i + 1];
                mesh[i] = originalX * cos - originalY * sin + x;
                mesh[i + 1] = originalX * sin + originalY * cos + y;
            }
        },
    },
    shape: {

        triangles: function (points: XY[], mesh: Float32Array): Float32Array {
            if (!mesh) mesh = new Float32Array(points.length * 2);
            for (let i = 0; i < points.length; i++) {
                mesh[i * 2] = points[i].x;
                mesh[i * 2 + 1] = points[i].y;
            }
            return mesh;
        },
        circle: function (params: { x?: number, y?: number, size?: number, resolution?: number, rotation?: number, start?: number, end?: number, mesh?: Float32Array }) {

            const { x = 0, y = 0, size = 0.1, resolution = 1, rotation = 0, start = 0, end = 1, mesh } = params;

            // Calculate required size
            const trianglesCount = Math.max(3, resolution);
            const requiredSize = trianglesCount * 3 * 2; // triangles * vertices per triangle * (x,y)

            // Create new mesh or use existing one
            const output_mesh = mesh || new Float32Array(requiredSize);

            const slice = (end - start) / resolution;

            let vertexIndex = 0;
            const position = { x, y };

            for (let i = 0; i < resolution; i++) {

                const r1 = start + slice * i + rotation;
                const r2 = start + slice * (i + 1) + rotation;

                // Get points on circumference
                const point1 = G2.project.circle({ position, distance: size, angle: r1 });
                const point2 = G2.project.circle({ position, distance: size, angle: r2 });

                // Triangle 1: point1 -> point2 -> center (counter-clockwise winding)
                // Vertex 1: point1
                output_mesh[vertexIndex++] = point1.x;
                output_mesh[vertexIndex++] = point1.y;

                // Vertex 2: point2  
                output_mesh[vertexIndex++] = point2.x;
                output_mesh[vertexIndex++] = point2.y;

                // Vertex 3: center
                output_mesh[vertexIndex++] = x;
                output_mesh[vertexIndex++] = y;
            }

            return output_mesh;
        },
        ring: function ({ x = 0, y = 0, size = 0.1, thickness = 0.03, resolution = 8, rotation = 0, start = 0, end = 1, mesh }: { x?: number, y?: number, size?: number, thickness?: number, resolution?: number, rotation?: number, start?: number, end?: number, mesh?: Float32Array }): Float32Array {

            const vertexCount = resolution * 6; // 2 triangles per segment
            const componentsPerVertex = 2;
            const totalComponents = vertexCount * componentsPerVertex;

            if (!mesh || mesh.length !== totalComponents) mesh = new Float32Array(totalComponents);

            const slice = (end - start) / resolution;
            const outer = size + thickness;
            const inner = size - thickness;

            let vertexIndex = 0;

            for (let i = 0; i < resolution; i++) {

                const r1 = start + slice * i + rotation;
                const r2 = start + slice * (i + 1) + rotation;

                const outer1 = G2.project.circle({ distance: outer, angle: r1 });
                const outer2 = G2.project.circle({ distance: outer, angle: r2 });
                const inner1 = G2.project.circle({ distance: inner, angle: r1 });
                const inner2 = G2.project.circle({ distance: inner, angle: r2 });

                // triangle 1: outer1 -> outer2 -> inner1
                mesh[vertexIndex++] = outer1.x + x; mesh[vertexIndex++] = outer1.y + y;
                mesh[vertexIndex++] = outer2.x + x; mesh[vertexIndex++] = outer2.y + y;
                mesh[vertexIndex++] = inner1.x + x; mesh[vertexIndex++] = inner1.y + y;

                // triangle 2: inner1 -> outer2 -> inner2
                mesh[vertexIndex++] = inner1.x + x; mesh[vertexIndex++] = inner1.y + y;
                mesh[vertexIndex++] = outer2.x + x; mesh[vertexIndex++] = outer2.y + y;
                mesh[vertexIndex++] = inner2.x + x; mesh[vertexIndex++] = inner2.y + y;
            }

            return mesh;
        },
        line: function (coords: Float32Array, width = 0.02, vertices?: Float32Array): Float32Array {  // [x1, y1, x2, y2, ...]

            const pointCount = coords.length / 2;
            const offsets = new Float32Array(pointCount * 4);
            let n;

            for (let i = 0; i < coords.length; i += 2) {

                const x = coords[i];
                const y = coords[i + 1];

                if (i === 0) {
                    n = M2.get.normals([x, y], [coords[i + 2], coords[i + 3]], width);
                } else if (i === coords.length - 2) {
                    n = M2.get.normals([coords[i - 2], coords[i - 1]], [x, y], width);
                } else {
                    n = M2.get.normals([coords[i - 2], coords[i - 1]], [coords[i + 2], coords[i + 3]], width);
                }

                const oi = i * 2;
                offsets[oi] = x - n[0]; offsets[oi + 1] = y - n[1];
                offsets[oi + 2] = x + n[0]; offsets[oi + 3] = y + n[1];
            }

            const result = vertices || new Float32Array((pointCount - 1) * 12);
            let idx = 0;

            for (let i = 0; i < (pointCount - 1) * 4; i += 4) {
                result[idx++] = offsets[i]; result[idx++] = offsets[i + 1];
                result[idx++] = offsets[i + 2]; result[idx++] = offsets[i + 3];
                result[idx++] = offsets[i + 4]; result[idx++] = offsets[i + 5];

                result[idx++] = offsets[i + 2]; result[idx++] = offsets[i + 3];
                result[idx++] = offsets[i + 6]; result[idx++] = offsets[i + 7];
                result[idx++] = offsets[i + 4]; result[idx++] = offsets[i + 5];
            }

            return result;
        },
        line_dashed: function (coords: Float32Array, width = 0.02, dash = 0.01, gap = 0.02): Float32Array {

            const point_count = coords.length / 2;
            if (point_count < 2) return new Float32Array(0);

            // cumulative arc lengths
            const distances = new Float32Array(point_count);
            for (let i = 1; i < point_count; i++) {
                const dx = coords[i * 2] - coords[(i - 1) * 2];
                const dy = coords[i * 2 + 1] - coords[(i - 1) * 2 + 1];
                distances[i] = distances[i - 1] + Math.sqrt(dx * dx + dy * dy);
            }

            const total_length = distances[point_count - 1];
            if (total_length === 0) return new Float32Array(0);

            const cycle = dash + gap;
            // upper bound: each dash produces at most (2 + intermediate_vertices) quad pairs
            // worst case every input vertex falls inside a dash → point_count quads + dash_count endpoint quads
            const dash_count = Math.ceil(total_length / cycle);
            const max_quads = point_count + dash_count * 2;
            const mesh = new Float32Array(max_quads * 12); // 2 triangles × 3 verts × 2 coords per quad
            let write = 0;

            let segment_index = 0; // tracks position along polyline across dashes
            let segment_start = 0;

            while (segment_start < total_length) {
                const segment_end = Math.min(segment_start + dash, total_length);

                // advance segment_index to the edge containing segment_start
                while (segment_index < point_count - 2 && distances[segment_index + 1] < segment_start) segment_index++;

                // previous normal-offset point for building quads
                let prev_lx = 0, prev_ly = 0, prev_rx = 0, prev_ry = 0;
                let has_prev = false;

                // emit a vertex: compute perpendicular offset and write quad with previous point
                const emit = (x: number, y: number, nx: number, ny: number) => {
                    const lx = x - nx, ly = y - ny, rx = x + nx, ry = y + ny;
                    if (has_prev) {
                        mesh[write++] = prev_lx; mesh[write++] = prev_ly;
                        mesh[write++] = prev_rx; mesh[write++] = prev_ry;
                        mesh[write++] = lx; mesh[write++] = ly;
                        mesh[write++] = prev_rx; mesh[write++] = prev_ry;
                        mesh[write++] = rx; mesh[write++] = ry;
                        mesh[write++] = lx; mesh[write++] = ly;
                    }
                    prev_lx = lx; prev_ly = ly; prev_rx = rx; prev_ry = ry;
                    has_prev = true;
                };

                // interpolate point and normal at a distance along the polyline
                const emit_at = (distance: number, edge: number) => {
                    const span = distances[edge + 1] - distances[edge];
                    const t = span > 0 ? (distance - distances[edge]) / span : 0;
                    const ax = coords[edge * 2], ay = coords[edge * 2 + 1];
                    const bx = coords[(edge + 1) * 2], by = coords[(edge + 1) * 2 + 1];
                    const px = -(by - ay), py = bx - ax;
                    const len = Math.sqrt(px * px + py * py);
                    const scale = len > 0 ? width / len : 0;
                    emit(ax + t * (bx - ax), ay + t * (by - ay), px * scale, py * scale);
                };

                // start point
                emit_at(segment_start, segment_index);

                // intermediate polyline vertices within this dash
                let k = segment_index + 1;
                while (k < point_count && distances[k] <= segment_end) {
                    // normal from neighboring edges
                    const prev_edge = k > 0 ? k - 1 : 0;
                    const next_edge = k < point_count - 1 ? k : k - 1;
                    const px = -(coords[(next_edge + 1) * 2 + 1] - coords[prev_edge * 2 + 1]);
                    const py = coords[(next_edge + 1) * 2] - coords[prev_edge * 2];
                    const len = Math.sqrt(px * px + py * py);
                    const scale = len > 0 ? width / len : 0;
                    emit(coords[k * 2], coords[k * 2 + 1], px * scale, py * scale);
                    k++;
                }

                // end point
                if (segment_end < total_length) {
                    const edge = Math.min(k > 0 ? k - 1 : 0, point_count - 2);
                    emit_at(segment_end, edge);
                }

                segment_start += cycle;
            }

            return mesh.subarray(0, write);
        },
        lines: function (segments: any[], width = 0.02, vertices?: Float32Array): Float32Array { //same as 'line' but with multiple arrays [[x1, y1, x2, y2, x3, y3], [x1, y1, x2, y2], ...]

            if (vertices) {
                let offset = 0;
                for (const coords of segments) {
                    const segLen = (coords.length / 2 - 1) * 12;
                    const sub = new Float32Array(vertices.buffer, (vertices.byteOffset + offset * 4), segLen);
                    this.line(coords, width, sub);
                    offset += segLen;
                }
                return vertices;
            }

            const combinedMesh = [];
            for (const coords of segments) {
                combinedMesh.push(...this.line(coords, width));
            }

            return new Float32Array(combinedMesh);
        },
        line_xy: function (params: { a: XY, b: XY, thickness?: number, vertices?: Float32Array }): Float32Array {

            const { vertices = new Float32Array(12), a, b, thickness = 0.01 } = params;

            // Calculate direction vector
            const dx = b.x - a.x;
            const dy = b.y - a.y;

            const length = Math.sqrt(dx * dx + dy * dy);
            if (length === 0) console.log("ERROR, points same position")

            // Normalize perpendicular vector
            const px = (-dy / length) * thickness;
            const py = (dx / length) * thickness;

            // Calculate the four corners of the thick line
            vertices[0] = a.x + px;
            vertices[1] = a.y + py;
            vertices[2] = b.x + px;
            vertices[3] = b.y + py;
            vertices[4] = b.x - px;
            vertices[5] = b.y - py;

            // Second triangle (reusing some vertices)
            vertices[6] = a.x + px;
            vertices[7] = a.y + py;
            vertices[8] = b.x - px;
            vertices[9] = b.y - py;
            vertices[10] = a.x - px;
            vertices[11] = a.y - py;

            return vertices;
        },
        lines_xy: function (params: { vertices?: Float32Array, ia: any[], thickness?: number, closed?: boolean }): Float32Array {
            const { vertices, ia, thickness = 0.01, closed = false } = params;
            const count = closed ? ia.length : ia.length - 1;
            const resultVertices = vertices || new Float32Array(count * 12);
            let v = 0;
            for (let i = 0; i < count; i++) {
                const a = ia[i].position;
                const b = ia[(i + 1) % ia.length].position;
                const dx = b.x - a.x, dy = b.y - a.y;
                const len = Math.hypot(dx, dy) || 1e-6;
                const px = (-dy / len) * thickness;
                const py = (dx / len) * thickness;
                const x1 = a.x + px, y1 = a.y + py;
                const x2 = b.x + px, y2 = b.y + py;
                const x3 = b.x - px, y3 = b.y - py;
                const x4 = a.x - px, y4 = a.y - py;
                resultVertices.set([x1, y1, x2, y2, x3, y3, x1, y1, x3, y3, x4, y4], v);
                v += 12;
            }
            return resultVertices;
        },
        lines_x_y: function (params: { x: number[], y: number[], thickness?: number, vertices?: Float32Array }): Float32Array {

            const { x, y, thickness = 0.01, vertices } = params;
            const resultVertices = vertices || new Float32Array((x.length - 1) * 12);
            const normals: [number, number][] = new Array(x.length);

            for (let i = 0; i < x.length; i++) {
                if (i === 0) {
                    normals[i] = M2.get.normals([x[i], y[i]], [x[i + 1], y[i + 1]], thickness) as [number, number];
                } else if (i === x.length - 1) {
                    normals[i] = M2.get.normals([x[i - 1], y[i - 1]], [x[i], y[i]], thickness) as [number, number];
                } else {
                    normals[i] = M2.get.normals([x[i - 1], y[i - 1]], [x[i + 1], y[i + 1]], thickness) as [number, number];
                }
            }
            for (let i = 0; i < x.length - 1; i++) {
                const index = i * 12;
                const n1 = normals[i], n2 = normals[i + 1];
                const left1 = [x[i] - n1[0], y[i] - n1[1]];
                const right1 = [x[i] + n1[0], y[i] + n1[1]];
                const left2 = [x[i + 1] - n2[0], y[i + 1] - n2[1]];
                const right2 = [x[i + 1] + n2[0], y[i + 1] + n2[1]];
                resultVertices[index + 0] = left1[0];
                resultVertices[index + 1] = left1[1];
                resultVertices[index + 2] = right1[0];
                resultVertices[index + 3] = right1[1];
                resultVertices[index + 4] = left2[0];
                resultVertices[index + 5] = left2[1];
                resultVertices[index + 6] = right1[0];
                resultVertices[index + 7] = right1[1];
                resultVertices[index + 8] = right2[0];
                resultVertices[index + 9] = right2[1];
                resultVertices[index + 10] = left2[0];
                resultVertices[index + 11] = left2[1];
            }
            return resultVertices;
        },
        rect: function (params: { width: number, height: number, vertices?: Float32Array }): Float32Array { //quad
            const { width = 0.2, height = 0.3, vertices } = params;
            const resultVertices = vertices || new Float32Array(12);
            const hw = width * 0.5;
            const hh = height * 0.5;
            resultVertices[0] = -hw;
            resultVertices[1] = -hh;
            resultVertices[2] = hw;
            resultVertices[3] = hh;
            resultVertices[4] = -hw;
            resultVertices[5] = hh;
            resultVertices[6] = -hw;
            resultVertices[7] = -hh;
            resultVertices[8] = hw;
            resultVertices[9] = -hh;
            resultVertices[10] = hw;
            resultVertices[11] = hh;
            return resultVertices;
        },
        rect_half: function (hw: number, hh: number, vertices?: Float32Array): Float32Array { //quad
            const resultVertices = vertices || new Float32Array(12);
            resultVertices[0] = -hw;
            resultVertices[1] = -hh;
            resultVertices[2] = hw;
            resultVertices[3] = hh;
            resultVertices[4] = -hw;
            resultVertices[5] = hh;
            resultVertices[6] = -hw;
            resultVertices[7] = -hh;
            resultVertices[8] = hw;
            resultVertices[9] = -hh;
            resultVertices[10] = hw;
            resultVertices[11] = hh;
            return resultVertices;
        },
        rect_half_xy: function (x: number, y: number, hw: number, hh: number, vertices?: Float32Array): Float32Array {
            const resultVertices = vertices || new Float32Array(12);
            resultVertices[0] = x - hw;
            resultVertices[1] = y - hh;
            resultVertices[2] = x + hw;
            resultVertices[3] = y + hh;
            resultVertices[4] = x - hw;
            resultVertices[5] = y + hh;
            resultVertices[6] = x - hw;
            resultVertices[7] = y - hh;
            resultVertices[8] = x + hw;
            resultVertices[9] = y - hh;
            resultVertices[10] = x + hw;
            resultVertices[11] = y + hh;
            return resultVertices;
        },
        rect_half_off: function (hw: number, hh: number, off_x: number, off_y: number, off_xx: number, off_yy: number, vertices?: Float32Array): Float32Array {
            const resultVertices = vertices || new Float32Array(12);
            resultVertices[0] = -hw + off_x;
            resultVertices[1] = -hh + off_y;
            resultVertices[2] = hw + off_xx;
            resultVertices[3] = hh + off_yy;
            resultVertices[4] = -hw + off_x;
            resultVertices[5] = hh + off_yy;
            resultVertices[6] = -hw + off_x;
            resultVertices[7] = -hh + off_y;
            resultVertices[8] = hw + off_xx;
            resultVertices[9] = -hh + off_y;
            resultVertices[10] = hw + off_xx;
            resultVertices[11] = hh + off_yy;
            return resultVertices;
        },
        rect_xx: function (x: number, xx: number, y: number, yy: number, vertices?: Float32Array): Float32Array {
            const resultVertices = vertices || new Float32Array(12);
            resultVertices[0] = x;
            resultVertices[1] = y;
            resultVertices[2] = xx;
            resultVertices[3] = y;
            resultVertices[4] = xx;
            resultVertices[5] = yy;
            resultVertices[6] = x;
            resultVertices[7] = y;
            resultVertices[8] = xx;
            resultVertices[9] = yy;
            resultVertices[10] = x;
            resultVertices[11] = yy;
            return resultVertices;
        },
        rect_xy: function (x: number, y: number, width: number, height: number, vertices?: Float32Array): Float32Array {
            const resultVertices = vertices || new Float32Array(12);
            const halfWidth = width / 2;
            const halfHeight = height / 2;
            resultVertices[0] = x - halfWidth;
            resultVertices[1] = y - halfHeight;
            resultVertices[2] = x + halfWidth;
            resultVertices[3] = y - halfHeight;
            resultVertices[4] = x + halfWidth;
            resultVertices[5] = y + halfHeight;
            resultVertices[6] = x - halfWidth;
            resultVertices[7] = y - halfHeight;
            resultVertices[8] = x + halfWidth;
            resultVertices[9] = y + halfHeight;
            resultVertices[10] = x - halfWidth;
            resultVertices[11] = y + halfHeight;
            return resultVertices;
        },
        rects_diagonals: function (boxes: { x: number, xx: number, y: number, yy: number }[], width = 0.02): Float32Array {
            const segments: number[][] = [];
            for (const { x, xx, y, yy } of boxes) {
                segments.push([x, y, xx, y, xx, yy, x, yy, x, y]);
                segments.push([x, y, xx, yy]);
                segments.push([xx, y, x, yy]);
            }
            return this.lines(segments, width);
        },
        frame: function (params: { width?: number, height?: number, border?: number, vertices?: Float32Array, centerpoint?: boolean }): Float32Array {
            const { width = 0.5, height = 0.3, border = 0.05, vertices, centerpoint = false } = params;
            const resultVertices = vertices || (centerpoint ? new Float32Array(60) : new Float32Array(48));
            const hw = width * 0.5;
            const hh = height * 0.5;
            const hb = border * 0.5;
            const outer = { left: -hw - hb, right: hw + hb, top: -hh - hb, bottom: hh + hb };
            const inner = { left: -hw + hb, right: hw - hb, top: -hh + hb, bottom: hh - hb };
            resultVertices[0] = outer.left; resultVertices[1] = outer.top;
            resultVertices[2] = inner.right; resultVertices[3] = inner.top;
            resultVertices[4] = inner.left; resultVertices[5] = inner.top;
            resultVertices[6] = outer.left; resultVertices[7] = outer.top;
            resultVertices[8] = outer.right; resultVertices[9] = outer.top;
            resultVertices[10] = inner.right; resultVertices[11] = inner.top;
            resultVertices[12] = outer.right; resultVertices[13] = outer.top;
            resultVertices[14] = inner.right; resultVertices[15] = inner.bottom;
            resultVertices[16] = inner.right; resultVertices[17] = inner.top;
            resultVertices[18] = outer.right; resultVertices[19] = outer.top;
            resultVertices[20] = outer.right; resultVertices[21] = outer.bottom;
            resultVertices[22] = inner.right; resultVertices[23] = inner.bottom;
            resultVertices[24] = inner.left; resultVertices[25] = inner.bottom;
            resultVertices[26] = inner.right; resultVertices[27] = inner.bottom;
            resultVertices[28] = outer.right; resultVertices[29] = outer.bottom;
            resultVertices[30] = inner.left; resultVertices[31] = inner.bottom;
            resultVertices[32] = outer.right; resultVertices[33] = outer.bottom;
            resultVertices[34] = outer.left; resultVertices[35] = outer.bottom;
            resultVertices[36] = inner.left; resultVertices[37] = inner.top;
            resultVertices[38] = inner.left; resultVertices[39] = inner.bottom;
            resultVertices[40] = outer.left; resultVertices[41] = outer.bottom;
            resultVertices[42] = outer.left; resultVertices[43] = outer.top;
            resultVertices[44] = inner.left; resultVertices[45] = inner.top;
            resultVertices[46] = outer.left; resultVertices[47] = outer.bottom;
            if (centerpoint) {
                resultVertices[48] = -border; resultVertices[49] = -border;
                resultVertices[50] = border; resultVertices[51] = border;
                resultVertices[52] = -border; resultVertices[53] = border;
                resultVertices[54] = -border; resultVertices[55] = -border;
                resultVertices[56] = border; resultVertices[57] = -border;
                resultVertices[58] = border; resultVertices[59] = border;
            }
            return resultVertices;
        },
        frame_half: function (params: { width?: number, height?: number, border?: number, vertices?: Float32Array }): Float32Array {
            const { width = 0.5, height = 0.3, border = 0.05, vertices } = params;
            const resultVertices = vertices || new Float32Array(48);
            const outer = { left: -width - border, right: width + border, top: -height - border, bottom: height + border };
            const inner = { left: -width + border, right: width - border, top: -height + border, bottom: height - border };
            resultVertices[0] = outer.left; resultVertices[1] = outer.top;
            resultVertices[2] = inner.right; resultVertices[3] = inner.top;
            resultVertices[4] = inner.left; resultVertices[5] = inner.top;
            resultVertices[6] = outer.left; resultVertices[7] = outer.top;
            resultVertices[8] = outer.right; resultVertices[9] = outer.top;
            resultVertices[10] = inner.right; resultVertices[11] = inner.top;
            resultVertices[12] = outer.right; resultVertices[13] = outer.top;
            resultVertices[14] = inner.right; resultVertices[15] = inner.bottom;
            resultVertices[16] = inner.right; resultVertices[17] = inner.top;
            resultVertices[18] = outer.right; resultVertices[19] = outer.top;
            resultVertices[20] = outer.right; resultVertices[21] = outer.bottom;
            resultVertices[22] = inner.right; resultVertices[23] = inner.bottom;
            resultVertices[24] = inner.left; resultVertices[25] = inner.bottom;
            resultVertices[26] = inner.right; resultVertices[27] = inner.bottom;
            resultVertices[28] = outer.right; resultVertices[29] = outer.bottom;
            resultVertices[30] = inner.left; resultVertices[31] = inner.bottom;
            resultVertices[32] = outer.right; resultVertices[33] = outer.bottom;
            resultVertices[34] = outer.left; resultVertices[35] = outer.bottom;
            resultVertices[36] = inner.left; resultVertices[37] = inner.top;
            resultVertices[38] = inner.left; resultVertices[39] = inner.bottom;
            resultVertices[40] = outer.left; resultVertices[41] = outer.bottom;
            resultVertices[42] = outer.left; resultVertices[43] = outer.top;
            resultVertices[44] = inner.left; resultVertices[45] = inner.top;
            resultVertices[46] = outer.left; resultVertices[47] = outer.bottom;
            return resultVertices;
        },
        frame_half_xy: function (params: { x?: number, y?: number, width?: number, height?: number, border?: number, vertices?: Float32Array }): Float32Array {
            const { x = 0, y = 0, width = 0.5, height = 0.3, border = 0.05, vertices } = params;
            const resultVertices = vertices || new Float32Array(48);
            const outerLeft = x - width - border;
            const outerRight = x + width + border;
            const outerTop = y - height - border;
            const outerBottom = y + height + border;
            const innerLeft = x - width + border;
            const innerRight = x + width - border;
            const innerTop = y - height + border;
            const innerBottom = y + height - border;
            resultVertices[0] = outerLeft; resultVertices[1] = outerTop;
            resultVertices[2] = innerRight; resultVertices[3] = innerTop;
            resultVertices[4] = innerLeft; resultVertices[5] = innerTop;
            resultVertices[6] = outerLeft; resultVertices[7] = outerTop;
            resultVertices[8] = outerRight; resultVertices[9] = outerTop;
            resultVertices[10] = innerRight; resultVertices[11] = innerTop;
            resultVertices[12] = outerRight; resultVertices[13] = outerTop;
            resultVertices[14] = outerRight; resultVertices[15] = outerBottom;
            resultVertices[16] = innerRight; resultVertices[17] = innerBottom;
            resultVertices[18] = outerRight; resultVertices[19] = outerTop;
            resultVertices[20] = innerRight; resultVertices[21] = innerBottom;
            resultVertices[22] = innerRight; resultVertices[23] = innerTop;
            resultVertices[24] = innerLeft; resultVertices[25] = innerBottom;
            resultVertices[26] = innerRight; resultVertices[27] = innerBottom;
            resultVertices[28] = outerRight; resultVertices[29] = outerBottom;
            resultVertices[30] = innerLeft; resultVertices[31] = innerBottom;
            resultVertices[32] = outerRight; resultVertices[33] = outerBottom;
            resultVertices[34] = outerLeft; resultVertices[35] = outerBottom;
            resultVertices[36] = innerLeft; resultVertices[37] = innerTop;
            resultVertices[38] = innerLeft; resultVertices[39] = innerBottom;
            resultVertices[40] = outerLeft; resultVertices[41] = outerBottom;
            resultVertices[42] = outerLeft; resultVertices[43] = outerTop;
            resultVertices[44] = innerLeft; resultVertices[45] = innerTop;
            resultVertices[46] = outerLeft; resultVertices[47] = outerBottom;
            return resultVertices;
        },
        clock_squares: function (params: {
            x?: number, y?: number, size?: number, dot_size?: number, hours?: number, start?: number, end?: number, rotation?: number, vertices?: Float32Array
        }): Float32Array {

            const { x = 0, y = 0, size = 0.3, dot_size = 0.05, hours = 12, start = 0, end = 1, rotation = 0, vertices } = params;

            const resultVertices = vertices || new Float32Array(hours * 6 * 2);
            const angle_step = (end - start) / hours;
            let vertexIndex = 0;

            for (let i = 0; i < hours; i++) {
                const angle = start + angle_step * i + rotation;
                const centerX = x + size * Math.cos(angle * M2.pp);
                const centerY = y + size * Math.sin(angle * M2.pp);

                const left = centerX - dot_size;
                const right = centerX + dot_size;
                const bottom = centerY - dot_size;
                const top = centerY + dot_size;

                // First triangle: top-left -> top-right -> bottom-left
                resultVertices[vertexIndex++] = left;
                resultVertices[vertexIndex++] = top;

                resultVertices[vertexIndex++] = right;
                resultVertices[vertexIndex++] = top;

                resultVertices[vertexIndex++] = left;
                resultVertices[vertexIndex++] = bottom;

                // Second triangle: top-right -> bottom-right -> bottom-left
                resultVertices[vertexIndex++] = right;
                resultVertices[vertexIndex++] = top;

                resultVertices[vertexIndex++] = right;
                resultVertices[vertexIndex++] = bottom;

                resultVertices[vertexIndex++] = left;
                resultVertices[vertexIndex++] = bottom;
            }

            return resultVertices;
        },
        clock_dots: function (params: { x?: number, y?: number, size?: number, dot_size?: number, hours?: number, start?: number, end?: number, resolution?: number, rotation?: number, vertices?: Float32Array }): Float32Array {

            const { x = 0, y = 0, size = 0.3, dot_size = 0.05, hours = 12, start = 0, end = 1, resolution = 12, rotation = 0, vertices } = params;
            const dot_length = resolution * 3 * 2;
            const resultVertices = vertices || new Float32Array(hours * dot_length);
            const angle_step = (end - start) / hours;

            for (let i = 0; i < hours; i++) {
                const angle = start + angle_step * i + rotation;
                const centerX = x + size * Math.cos(angle * M2.pp);
                const centerY = y + size * Math.sin(angle * M2.pp);

                const dot = M2.shape.circle({ x: centerX, y: centerY, size: dot_size, resolution });
                const index = i * dot_length;

                for (let j = 0; j < dot_length; j++) {
                    resultVertices[index + j] = dot[j];
                }
            }

            return resultVertices;
        },
        clock_rects: function (params: { x?: number, y?: number, size?: number, rect_wide?: number, rect_long?: number, hours?: number, start?: number, end?: number, rotation?: number, vertices?: Float32Array }): Float32Array {

            const { x = 0, y = 0, size = 0.3, rect_wide = 0.01, rect_long = 0.05, hours = 12, start = 0, end = 1, rotation = -0.25, vertices } = params;
            const resultVertices = vertices || new Float32Array(hours * 12);

            const angle_step = (end - start) / hours;
            for (let i = 0; i < hours; i++) {
                const angle = (start + angle_step * i + rotation) * M2.pp;
                const cos = Math.cos(angle);
                const sin = Math.sin(angle);
                const center_x = x + size * cos;
                const center_y = y + size * sin;

                // perpendicular to radial direction
                const px = -sin * rect_wide;
                const py = cos * rect_wide;
                // radial direction
                const rx = cos * rect_long;
                const ry = sin * rect_long;

                // four corners: inner-left, inner-right, outer-left, outer-right
                const ilx = center_x - rx - px;
                const ily = center_y - ry - py;
                const irx = center_x - rx + px;
                const iry = center_y - ry + py;
                const olx = center_x + rx - px;
                const oly = center_y + ry - py;
                const orx = center_x + rx + px;
                const ory = center_y + ry + py;

                const index = i * 12;
                // triangle 1
                resultVertices[index] = ilx;
                resultVertices[index + 1] = ily;
                resultVertices[index + 2] = irx;
                resultVertices[index + 3] = iry;
                resultVertices[index + 4] = olx;
                resultVertices[index + 5] = oly;
                // triangle 2
                resultVertices[index + 6] = irx;
                resultVertices[index + 7] = iry;
                resultVertices[index + 8] = orx;
                resultVertices[index + 9] = ory;
                resultVertices[index + 10] = olx;
                resultVertices[index + 11] = oly;
            }

            return resultVertices;
        },
        box: function (params: { x?: number, xx?: number, y?: number, yy?: number, border?: number, vertices?: Float32Array, centerpoint?: number | boolean }): Float32Array {

            const { x = -0.2, xx = 0.3, y = -0.3, yy = 0.5, border = 0.05, vertices, centerpoint = 0.1 } = params;

            const hasCenterpoint = typeof centerpoint === 'number' ? centerpoint > 0 : centerpoint;
            const centerpointSize = typeof centerpoint === 'number' ? centerpoint : 0.1;

            const resultVertices = vertices || (hasCenterpoint ? new Float32Array(60) : new Float32Array(48));
            const hb = border * 0.5;

            const outer = { left: x - hb, right: xx + hb, top: y - hb, bottom: yy + hb };
            const inner = { left: x + hb, right: xx - hb, top: y + hb, bottom: yy - hb };

            // Top-left corner (clockwise)
            resultVertices[0] = outer.left; resultVertices[1] = outer.top;
            resultVertices[2] = inner.left; resultVertices[3] = inner.top;
            resultVertices[4] = inner.right; resultVertices[5] = inner.top;

            resultVertices[6] = outer.left; resultVertices[7] = outer.top;
            resultVertices[8] = inner.right; resultVertices[9] = inner.top;
            resultVertices[10] = outer.right; resultVertices[11] = outer.top;

            // Top-right corner (clockwise)
            resultVertices[12] = outer.right; resultVertices[13] = outer.top;
            resultVertices[14] = inner.right; resultVertices[15] = inner.top;
            resultVertices[16] = inner.right; resultVertices[17] = inner.bottom;

            resultVertices[18] = outer.right; resultVertices[19] = outer.top;
            resultVertices[20] = inner.right; resultVertices[21] = inner.bottom;
            resultVertices[22] = outer.right; resultVertices[23] = outer.bottom;

            // Bottom-right corner (clockwise)
            resultVertices[24] = outer.right; resultVertices[25] = outer.bottom;
            resultVertices[26] = inner.right; resultVertices[27] = inner.bottom;
            resultVertices[28] = inner.left; resultVertices[29] = inner.bottom;

            resultVertices[30] = outer.right; resultVertices[31] = outer.bottom;
            resultVertices[32] = inner.left; resultVertices[33] = inner.bottom;
            resultVertices[34] = outer.left; resultVertices[35] = outer.bottom;

            // Bottom-left corner (clockwise)
            resultVertices[36] = outer.left; resultVertices[37] = outer.bottom;
            resultVertices[38] = inner.left; resultVertices[39] = inner.bottom;
            resultVertices[40] = inner.left; resultVertices[41] = inner.top;

            resultVertices[42] = outer.left; resultVertices[43] = outer.bottom;
            resultVertices[44] = inner.left; resultVertices[45] = inner.top;
            resultVertices[46] = outer.left; resultVertices[47] = outer.top;

            if (hasCenterpoint && typeof centerpointSize === 'number') {
                const cx = (x + xx) * 0.5;
                const cy = (y + yy) * 0.5;
                const c_x = cx - centerpointSize;
                const c_xx = cx + centerpointSize;
                const c_y = cy - centerpointSize;
                const c_yy = cy + centerpointSize;

                // Center point - first triangle (clockwise)
                resultVertices[48] = c_x; resultVertices[49] = c_y;
                resultVertices[50] = c_x; resultVertices[51] = c_yy;
                resultVertices[52] = c_xx; resultVertices[53] = c_y;

                // Center point - second triangle (clockwise)
                resultVertices[54] = c_xx; resultVertices[55] = c_y;
                resultVertices[56] = c_x; resultVertices[57] = c_yy;
                resultVertices[58] = c_xx; resultVertices[59] = c_yy;
            }

            return resultVertices;
        },
        ellipse: function (params: { x?: number, y?: number, width?: number, height?: number, resolution?: number, rotation?: number, start?: number, end?: number, vertices?: Float32Array }): Float32Array {

            const { x = 0, y = 0, width = 0.2, height = 0.1, resolution = 8, rotation = 0, start = 0, end = 1, vertices } = params;

            let normalizedEnd = end;
            const arcLength = end - start;

            if (arcLength >= 1) {
                normalizedEnd = start + 1;
            } else if (arcLength <= 0) {
                normalizedEnd = start + 1;
            }

            const segments = Math.max(3, resolution);
            const angleRange = (normalizedEnd - start) * M2.pp;
            const angleStep = angleRange / segments;

            const cosRot = Math.cos(rotation * M2.pp);
            const sinRot = Math.sin(rotation * M2.pp);

            const result = vertices || new Float32Array(segments * 6);
            let idx = 0;

            for (let i = 0; i < segments; i++) {
                const angle0 = start * M2.pp + angleStep * i;
                const angle1 = start * M2.pp + angleStep * (i + 1);

                const xx0 = Math.cos(angle0) * width;
                const yy0 = Math.sin(angle0) * height;
                const xx1 = Math.cos(angle1) * width;
                const yy1 = Math.sin(angle1) * height;

                result[idx++] = x;
                result[idx++] = y;
                result[idx++] = x + xx0 * cosRot - yy0 * sinRot;
                result[idx++] = y + xx0 * sinRot + yy0 * cosRot;
                result[idx++] = x + xx1 * cosRot - yy1 * sinRot;
                result[idx++] = y + xx1 * sinRot + yy1 * cosRot;
            }

            return result;
        },
        ellipse_outline: function (params: { x?: number, y?: number, width?: number, height?: number, thickness?: number, resolution?: number, rotation?: number, start?: number, end?: number, vertices?: Float32Array }): Float32Array {

            const { x = 0, y = 0, width = 0.2, height = 0.1, thickness = 0.05, resolution = 8, rotation = 0, start = 0, end = 1, vertices } = params;

            let normalizedStart = start % 1;
            let normalizedEnd = end % 1;
            if (normalizedEnd < normalizedStart) normalizedEnd += 1;
            if (normalizedEnd - normalizedStart <= 0) normalizedEnd = normalizedStart + 1;

            const segments = Math.max(3, resolution);
            const angleRange = (normalizedEnd - normalizedStart) * M2.pp;
            const angleStep = angleRange / segments;

            const rotationRad = rotation * M2.pp;
            const cosRot = Math.cos(rotationRad);
            const sinRot = Math.sin(rotationRad);

            const ringVerts = new Float32Array((segments + 1) * 4);

            for (let i = 0; i <= segments; i++) {
                const angle = normalizedStart * M2.pp + i * angleStep;
                const cosA = Math.cos(angle);
                const sinA = Math.sin(angle);

                const ox = cosA * (width + thickness);
                const oy = sinA * (height + thickness);
                const idx = i * 4;
                ringVerts[idx] = x + ox * cosRot - oy * sinRot;
                ringVerts[idx + 1] = y + ox * sinRot + oy * cosRot;

                const ix = cosA * Math.max(0.001, width - thickness);
                const iy = sinA * Math.max(0.001, height - thickness);
                ringVerts[idx + 2] = x + ix * cosRot - iy * sinRot;
                ringVerts[idx + 3] = y + ix * sinRot + iy * cosRot;
            }

            const result = vertices || new Float32Array(segments * 12);
            let idx = 0;

            for (let i = 0; i < segments; i++) {

                const o1 = i * 4;
                const o2 = (i + 1) * 4;

                result[idx++] = ringVerts[o1]; result[idx++] = ringVerts[o1 + 1];
                result[idx++] = ringVerts[o1 + 2]; result[idx++] = ringVerts[o1 + 3];
                result[idx++] = ringVerts[o2]; result[idx++] = ringVerts[o2 + 1];

                result[idx++] = ringVerts[o1 + 2]; result[idx++] = ringVerts[o1 + 3];
                result[idx++] = ringVerts[o2 + 2]; result[idx++] = ringVerts[o2 + 3];
                result[idx++] = ringVerts[o2]; result[idx++] = ringVerts[o2 + 1];
            }

            return result;
        },
        arc: function (params: { x?: number, y?: number, width?: number, height?: number, curvature?: number, resolution?: number, rotation?: number, vertices?: Float32Array }): Float32Array {

            const { x = 0, y = 0, width = 0.4, height = 0.2, curvature = 0.75, resolution = 12, rotation = 0, vertices } = params;

            const curveHeight = width * curvature;
            const result = vertices || new Float32Array(resolution * 12);
            let idx = 0;

            for (let i = 0; i < resolution; i++) {
                const t0 = i / resolution;
                const t1 = (i + 1) / resolution;

                const x0 = -width + t0 * width * 2;
                const x1 = -width + t1 * width * 2;

                const y0b = -height + curveHeight * Math.sqrt(1 - Math.pow(2 * t0 - 1, 2));
                const y0t = height + curveHeight * Math.sqrt(1 - Math.pow(2 * t0 - 1, 2));

                const y1b = -height + curveHeight * Math.sqrt(1 - Math.pow(2 * t1 - 1, 2));
                const y1t = height + curveHeight * Math.sqrt(1 - Math.pow(2 * t1 - 1, 2));

                result[idx++] = x0; result[idx++] = y0b;
                result[idx++] = x1; result[idx++] = y1b;
                result[idx++] = x0; result[idx++] = y0t;

                result[idx++] = x0; result[idx++] = y0t;
                result[idx++] = x1; result[idx++] = y1b;
                result[idx++] = x1; result[idx++] = y1t;
            }

            if (rotation) M2.set.rotates(result, rotation);
            M2.set.offsets(result, x, y);

            return result;
        },
        double_arc: function (params: { x?: number, y?: number, width?: number, height?: number, curvature?: number, resolution?: number, rotation?: number, vertices?: Float32Array }): Float32Array {

            const { x = 0, y = 0, width = 0.4, height = 0.2, curvature = 0.75, resolution = 12, rotation = 0, vertices } = params;

            const curveHeight = width * -curvature;
            const result = vertices || new Float32Array(resolution * 12);
            let idx = 0;

            for (let i = 0; i < resolution; i++) {
                const t0 = i / resolution;
                const t1 = (i + 1) / resolution;

                const x0 = -width + t0 * width * 2;
                const x1 = -width + t1 * width * 2;

                const curveOffset0 = curveHeight * Math.sqrt(1 - Math.pow(2 * t0 - 1, 2));
                const curveOffset1 = curveHeight * Math.sqrt(1 - Math.pow(2 * t1 - 1, 2));

                const y0b = -height + curveOffset0;
                const y1b = -height + curveOffset1;

                const y0t = height - curveOffset0;
                const y1t = height - curveOffset1;

                result[idx++] = x0; result[idx++] = y0b;
                result[idx++] = x1; result[idx++] = y1b;
                result[idx++] = x0; result[idx++] = y0t;

                result[idx++] = x0; result[idx++] = y0t;
                result[idx++] = x1; result[idx++] = y1b;
                result[idx++] = x1; result[idx++] = y1t;
            }

            if (rotation) M2.set.rotates(result, rotation);
            M2.set.offsets(result, x, y);

            return result;
        },
        moon: function (params: { x?: number, y?: number, width?: number, height?: number, curvature?: number, resolution?: number, rotation?: number, vertices?: Float32Array } = {}): Float32Array {

            const { x = 0.3, y = 0.1, width = 0.8, height = 0.5, curvature = 0.75, resolution = 48, rotation = 0, vertices } = params;
            const curveHeight = height * curvature;
            const result = vertices || new Float32Array(resolution * 12);
            let idx = 0;

            for (let i = 0; i < resolution; i++) {
                const t0 = i / resolution;
                const t1 = (i + 1) / resolution;

                const x0 = -width * 0.5 + t0 * width;
                const x1 = -width * 0.5 + t1 * width;

                const y0_outer = curveHeight * Math.sqrt(1 - Math.pow(2 * t0 - 1, 2));
                const y1_outer = curveHeight * Math.sqrt(1 - Math.pow(2 * t1 - 1, 2));

                const innerCurveHeight = curveHeight * 0.5;
                const y0_inner = innerCurveHeight * Math.sqrt(1 - Math.pow(2 * t0 - 1, 2));
                const y1_inner = innerCurveHeight * Math.sqrt(1 - Math.pow(2 * t1 - 1, 2));

                result[idx++] = x0; result[idx++] = y0_outer;
                result[idx++] = x1; result[idx++] = y1_outer;
                result[idx++] = x0; result[idx++] = y0_inner;

                result[idx++] = x0; result[idx++] = y0_inner;
                result[idx++] = x1; result[idx++] = y1_outer;
                result[idx++] = x1; result[idx++] = y1_inner;
            }

            if (rotation) M2.set.rotates(result, rotation);
            M2.set.offsets(result, x, y);

            return result;
        },
        polygon: function (params: { points?: XY[], vertices?: Float32Array }): Float32Array {

            const { points = [], vertices } = params;

            const flatPoints = Conv.xy_flat(points);
            const indices = earcut(flatPoints);

            const requiredLength = indices.length * 2;
            const resultVertices = vertices && vertices.length >= requiredLength ? vertices : new Float32Array(requiredLength);

            for (let i = 0; i < indices.length; i++) {
                const pointIndex = indices[i];
                resultVertices[i * 2] = flatPoints[pointIndex * 2];
                resultVertices[i * 2 + 1] = flatPoints[pointIndex * 2 + 1];
            }

            return resultVertices;
        },
        polygon_fan: function (points: XY[], vertices?: Float32Array): Float32Array {

            const resultVertices = vertices || new Float32Array((points.length - 1) * 6);

            for (let i = 1; i < points.length - 1; i++) {
                const vertexIndex = (i - 1) * 6;
                resultVertices[vertexIndex + 0] = points[0].x;
                resultVertices[vertexIndex + 1] = points[0].y;
                resultVertices[vertexIndex + 2] = points[i].x;
                resultVertices[vertexIndex + 3] = points[i].y;
                resultVertices[vertexIndex + 4] = points[i + 1].x;
                resultVertices[vertexIndex + 5] = points[i + 1].y;
            }

            return resultVertices;
        },
        snake: function (params: { points?: XY[], vertices?: Float32Array }): Float32Array {

            const { points = [], vertices } = params;
            const resultVertices = vertices || new Float32Array(points.length * 3 * 2);
            const half = Math.floor(points.length / 2);

            for (let i = 0; i < half; i++) {
                const topCurrent = i;
                const topNext = i + 1;
                const bottomCurrent = points.length - i - 1;
                const bottomNext = points.length - i - 2;

                const u = i * 12;

                resultVertices[u + 0] = points[topCurrent].x;
                resultVertices[u + 1] = points[topCurrent].y;
                resultVertices[u + 2] = points[topNext].x;
                resultVertices[u + 3] = points[topNext].y;
                resultVertices[u + 4] = points[bottomCurrent].x;
                resultVertices[u + 5] = points[bottomCurrent].y;

                resultVertices[u + 6] = points[topNext].x;
                resultVertices[u + 7] = points[topNext].y;
                resultVertices[u + 8] = points[bottomNext].x;
                resultVertices[u + 9] = points[bottomNext].y;
                resultVertices[u + 10] = points[bottomCurrent].x;
                resultVertices[u + 11] = points[bottomCurrent].y;
            }

            return resultVertices;
        },

        // generate a stroke mesh: ribbon between path points using convex hull of brush shape,
        // with full brush stamps at start, end, and sharp corners
        stroke: function (params: { x: number[], y: number[], shape: number[], size: number, angles?: number[], sizes?: number[] }): Float32Array {
            const { x, y, shape, size } = params;
            const n = x.length;

            if (n === 0) return new Float32Array(0);

            // pre-scale brush vertices, compute hull for ribbon
            const brush_pts: [number, number][] = [];
            for (let i = 0; i < shape.length; i += 2) {
                brush_pts.push([shape[i] * size, shape[i + 1] * size]);
            }
            const hull = M2.compute.convex_hull(brush_pts);
            const hn = hull.length;

            // per-point size multiplier (1 = full brush size)
            const sizes = params.sizes && params.sizes.length === n ? params.sizes : null;

            // per-point rotation: use caller-supplied angles or derive from path tangent
            const angles: number[] = params.angles && params.angles.length === n
                ? params.angles
                : [];
            if (angles.length === 0) {
                for (let i = 0; i < n; i++) {
                    if (i < n - 1) {
                        angles.push(Math.atan2(y[i + 1] - y[i], x[i + 1] - x[i]));
                    } else {
                        angles.push(i > 0 ? angles[i - 1] : 0);
                    }
                }
            }

            // place hull at a path point, rotated to tangent
            function place_hull(idx: number): [number, number][] {
                const px = x[idx], py = y[idx];
                const s = sizes ? sizes[idx] : 1;
                const cos = Math.cos(angles[idx]), sin = Math.sin(angles[idx]);
                return hull.map(([hx, hy]) => [
                    px + (hx * s) * cos - (hy * s) * sin,
                    py + (hx * s) * sin + (hy * s) * cos
                ] as [number, number]);
            }

            // stamp original brush triangles at a path point, rotated to tangent
            function stamp(idx: number, out: number[]) {
                const px = x[idx], py = y[idx];
                const s = sizes ? sizes[idx] : 1;
                const cos = Math.cos(angles[idx]), sin = Math.sin(angles[idx]);
                for (let j = 0; j < shape.length; j += 2) {
                    const sx = shape[j] * size * s, sy = shape[j + 1] * size * s;
                    out.push(px + sx * cos - sy * sin, py + sx * sin + sy * cos);
                }
            }

            // detect sharp corner (angle change > 45 deg)
            function is_corner(idx: number): boolean {
                if (idx <= 0 || idx >= n - 1) return false;
                let d = Math.abs(angles[idx] - angles[idx - 1]);
                if (d > Math.PI) d = 2 * Math.PI - d;
                return d > Math.PI / 4;
            }

            const out: number[] = [];

            // single point: just stamp
            if (n === 1) { stamp(0, out); return new Float32Array(out); }

            // stamp at start
            stamp(0, out);

            // ribbon: connect consecutive hull placements
            for (let i = 0; i < n - 1; i++) {
                const hA = place_hull(i);
                const hB = place_hull(i + 1);
                for (let j = 0; j < hn; j++) {
                    const j1 = (j + 1) % hn;
                    out.push(
                        hA[j][0], hA[j][1], hA[j1][0], hA[j1][1], hB[j][0], hB[j][1],
                        hA[j1][0], hA[j1][1], hB[j1][0], hB[j1][1], hB[j][0], hB[j][1]
                    );
                }
                // stamp at sharp corners
                if (is_corner(i + 1)) stamp(i + 1, out);
            }

            // stamp at end
            stamp(n - 1, out);

            return new Float32Array(out);
        },
    },
    compute: {
        merge: function (mesh1: number[], mesh2: number[]): number[] {
            const triangles1 = earcut(mesh1);
            const triangles2 = earcut(mesh2).map((i: number) => i + mesh1.length);
            const combinedTriangles = [...triangles1, ...triangles2];
            return combinedTriangles;
        },
        convex_hull: function (pts: [number, number][]): [number, number][] { // 2D convex hull (Graham scan), returns vertices in CCW order
            const seen = new Set<string>();
            const u: [number, number][] = [];
            for (const p of pts) {
                const k = p[0] + ',' + p[1];
                if (!seen.has(k)) { seen.add(k); u.push(p); }
            }
            if (u.length <= 2) return u;
            let lo = 0;
            for (let i = 1; i < u.length; i++) {
                if (u[i][1] < u[lo][1] || (u[i][1] === u[lo][1] && u[i][0] < u[lo][0])) lo = i;
            }
            [u[0], u[lo]] = [u[lo], u[0]];
            const piv = u[0];
            u.sort((a, b) => {
                if (a === piv) return -1;
                if (b === piv) return 1;
                const cross = (a[0] - piv[0]) * (b[1] - piv[1]) - (a[1] - piv[1]) * (b[0] - piv[0]);
                if (cross !== 0) return -cross;
                return (a[0] - piv[0]) ** 2 + (a[1] - piv[1]) ** 2 - ((b[0] - piv[0]) ** 2 + (b[1] - piv[1]) ** 2);
            });
            const stack: [number, number][] = [];
            for (const p of u) {
                while (stack.length >= 2) {
                    const a = stack[stack.length - 2], b = stack[stack.length - 1];
                    if ((b[0] - a[0]) * (p[1] - a[1]) - (b[1] - a[1]) * (p[0] - a[0]) <= 0) stack.pop();
                    else break;
                }
                stack.push(p);
            }
            return stack;
        },
        mesh_aabb: function (mesh: Float32Array): [number, number, number, number] {
            let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
            for (let i = 0; i < mesh.length; i += 2) {
                if (mesh[i] < minX) minX = mesh[i];
                if (mesh[i] > maxX) maxX = mesh[i];
                if (mesh[i + 1] < minY) minY = mesh[i + 1];
                if (mesh[i + 1] > maxY) maxY = mesh[i + 1];
            }
            return [minX, minY, maxX, maxY];
        },
        tri_aabb: function (mesh: Float32Array, i: number): [number, number, number, number] {
            const o = i * 6;
            const x0 = mesh[o], y0 = mesh[o + 1];
            const x1 = mesh[o + 2], y1 = mesh[o + 3];
            const x2 = mesh[o + 4], y2 = mesh[o + 5];
            return [
                Math.min(x0, x1, x2), Math.min(y0, y1, y2),
                Math.max(x0, x1, x2), Math.max(y0, y1, y2)
            ];
        },
        aabb_overlap: function (a: [number, number, number, number], b: [number, number, number, number]): boolean {
            return a[0] <= b[2] && a[2] >= b[0] && a[1] <= b[3] && a[3] >= b[1];
        },
        tri_area: function (mesh: Float32Array, i: number): number {
            const o = i * 6;
            const ax = mesh[o], ay = mesh[o + 1];
            const bx = mesh[o + 2], by = mesh[o + 3];
            const cx = mesh[o + 4], cy = mesh[o + 5];
            return Math.abs((bx - ax) * (cy - ay) - (cx - ax) * (by - ay)) * 0.5;
        },
        // do two segment vectors point the same way to within `min_cos`?
        // (dot of the normalized vectors >= min_cos). Used to drop redundant
        // points on near-straight runs of a path. Zero-length vectors → false.
        collinear: function (ax: number, ay: number, bx: number, by: number, min_cos: number): boolean {
            const alen = Math.sqrt(ax * ax + ay * ay);
            const blen = Math.sqrt(bx * bx + by * by);
            if (alen === 0 || blen === 0) return false;
            return (ax * bx + ay * by) / (alen * blen) >= min_cos;
        },
        // union a triangle-list mesh into its outline rings (outer + holes); if the union throws on degenerate input, fall back to the raw triangle multipolygon, which difference() and nonzero-winding fills still handle correctly
        mesh_outline: function (mesh: Float32Array): MeshMultiPolygon {
            const multi = mesh_to_multipolygon(M2.compute.remove_degenerate(mesh));
            if (multi.length <= 1) return multi;
            try {
                return polygonClipping.union(multi[0], ...multi.slice(1));
            } catch {
                return multi;
            }
        },
        remove_degenerate: function (mesh: Float32Array): Float32Array {
            const count = Math.floor(mesh.length / 6);
            const out: number[] = [];
            for (let i = 0; i < count; i++) {
                if (M2.compute.tri_area(mesh, i) >= TINY_TRI_AREA) {
                    const o = i * 6;
                    for (let k = 0; k < 6; k++) out.push(mesh[o + k]);
                }
            }
            return new Float32Array(out);
        },
        reduce: function (mesh: Float32Array, min_dist: number): Float32Array {
            if (min_dist <= 0 || mesh.length < 6) return mesh;
            const dist_sq = min_dist * min_dist;
            const count = Math.floor(mesh.length / 6);

            const unique: number[] = [];
            const index_map = new Int32Array(mesh.length / 2);

            for (let i = 0; i < mesh.length; i += 2) {
                const x = mesh[i], y = mesh[i + 1];
                let found = -1;
                for (let j = 0; j < unique.length; j += 2) {
                    const dx = x - unique[j], dy = y - unique[j + 1];
                    if (dx * dx + dy * dy < dist_sq) { found = j; break; }
                }
                if (found >= 0) {
                    index_map[i / 2] = found / 2;
                } else {
                    index_map[i / 2] = unique.length / 2;
                    unique.push(x, y);
                }
            }

            const out: number[] = [];
            for (let t = 0; t < count; t++) {
                const base = t * 3;
                const i0 = index_map[base], i1 = index_map[base + 1], i2 = index_map[base + 2];
                if (i0 === i1 || i1 === i2 || i0 === i2) continue;
                out.push(unique[i0 * 2], unique[i0 * 2 + 1]);
                out.push(unique[i1 * 2], unique[i1 * 2 + 1]);
                out.push(unique[i2 * 2], unique[i2 * 2 + 1]);
            }
            return new Float32Array(out);
        },
        simplify: function (mesh: Float32Array): Float32Array {
            const count = Math.floor(mesh.length / 6);
            if (count <= 1) return mesh;
            const multi = mesh_to_multipolygon(mesh);
            try {
                const unified = polygonClipping.union(multi[0], ...multi.slice(1));
                const result = triangulate_multi(unified);
                if (result.length > 0) return M2.compute.remove_degenerate(result);
            } catch { /* fall through */ }
            return mesh;
        },
        prepare_subtract: function (eraser: Float32Array) {
            return {
                poly: M2.compute.mesh_outline(eraser),
                aabb: M2.compute.mesh_aabb(eraser)
            };
        },

        subtract_prepared: function (target: Float32Array, prepared: { poly: MeshMultiPolygon; aabb: [number, number, number, number] }): Float32Array {
            if (prepared.poly.length === 0) return target;

            // cheap reject: if the eraser bbox doesn't overlap the target bbox, the
            // eraser can't cut it — return the original (identical) mesh so callers'
            // "length unchanged ⇒ skip" fast path still fires.
            if (!M2.compute.aabb_overlap(M2.compute.mesh_aabb(target), prepared.aabb)) return target;

            // Fast path: the target is triangle soup but came from a union, so rebuild its
            // outline and do ONE difference against the eraser — collapsing N per-triangle
            // clips into a single one. polygonClipping can throw on degenerate (near-
            // coincident) input though, so on any failure fall back to the per-triangle
            // path below, which clips each triangle independently and skips failures.
            try {
                const target_poly = M2.compute.mesh_outline(target);
                if (target_poly.length > 0) {
                    const diff = polygonClipping.difference(target_poly, prepared.poly);
                    if (diff.length === 0) return new Float32Array(0); // fully erased
                    return M2.compute.remove_degenerate(triangulate_multi(diff));
                }
            } catch { /* fall through to per-triangle */ }

            // Robust fallback: clip every triangle separately; a throw on one triangle
            // just keeps that triangle, so a single degenerate edge can't fail the stroke.
            const target_count = Math.floor(target.length / 6);
            const out: number[] = [];
            for (let i = 0; i < target_count; i++) {
                if (!M2.compute.aabb_overlap(M2.compute.tri_aabb(target, i), prepared.aabb)) {
                    const o = i * 6;
                    for (let k = 0; k < 6; k++) out.push(target[o + k]);
                    continue;
                }
                let diff: MeshMultiPolygon;
                try {
                    diff = polygonClipping.difference(tri_to_polygon(target, i), prepared.poly);
                } catch {
                    const o = i * 6;
                    for (let k = 0; k < 6; k++) out.push(target[o + k]);
                    continue;
                }
                if (diff.length === 0) continue;
                const tris = triangulate_multi(diff);
                for (let k = 0; k < tris.length; k++) out.push(tris[k]);
            }
            return M2.compute.simplify(new Float32Array(out));
        },

        subtract: function (target: Float32Array, eraser: Float32Array): Float32Array {
            return M2.compute.subtract_prepared(target, M2.compute.prepare_subtract(eraser));
        }
    }
};